// Pantallas LCD de caracteres con controlador HD44780: conexión paralela (16 pines) e I²C (PCF8574).
import { definePart, PITCH } from './registry.js';

export const LCD_SIZES = {
  '16x2': { cols: 16, rows: 2, w: 80, d: 36, bezel: [71.2, 25.2], window: [64.5, 15.6], bezelZ: 2.4, holes: [37.5, 15.5] },
  '20x4': { cols: 20, rows: 4, w: 98, d: 60, bezel: [97, 40], window: [77, 26.5], bezelZ: 3.2, holes: [46.5, 27.5] },
};

export const LCD_COLORS = {
  blue: { name: 'Azul (letras blancas)', bg: '#2245d6', bgOff: '#0f1c4a', on: '#e9f1ff', off: '#2f55e8' },
  green: { name: 'Verde (letras oscuras)', bg: '#9fca3b', bgOff: '#3e4d1c', on: '#1d2b10', off: '#94bf36' },
  white: { name: 'Negra (letras blancas)', bg: '#141922', bgOff: '#0b0d12', on: '#f3f6fa', off: '#1c222d' },
};

export const LCD_PCB_Y = 1.5;
export const LCD_I2C_PCB_Y = 12;
const PIN_NAMES = ['VSS', 'VDD', 'V0', 'RS', 'RW', 'E', 'D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'A', 'K'];
const PIN_LABELS = {
  VSS: 'VSS (GND)', VDD: 'VDD (5 V)', V0: 'V0 (contraste)', RS: 'RS (registro)', RW: 'RW (lectura/escritura)', E: 'E (habilitar)',
  A: 'A (luz de fondo +)', K: 'K (luz de fondo −)',
};

export function lcdSize(props) {
  return LCD_SIZES[props.size] || LCD_SIZES['16x2'];
}

/** x del pin 1 del conector de 16 pines (borde superior izquierdo). */
export function lcdHeaderX(size) {
  return -size.w / 2 + 9.5;
}

const sizeProp = {
  key: 'size', label: 'Tamaño', type: 'select', default: '16x2', structural: true,
  options: [{ value: '16x2', label: '16 × 2' }, { value: '20x4', label: '20 × 4' }],
};
const colorProp = {
  key: 'color', label: 'Pantalla', type: 'select', default: 'blue',
  options: Object.entries(LCD_COLORS).map(([value, c]) => ({ value, label: c.name, color: c.bg })),
};

definePart({
  type: 'lcd',
  name: 'LCD de caracteres',
  category: 'luces',
  description: 'Pantalla HD44780 con conexión paralela (librería LiquidCrystal). Lo usual: modo de 4 bits con RS, E y D4–D7.',
  keywords: 'pantalla display 1602 2004 liquidcrystal hd44780',
  props: [sizeProp, colorProp],
  variants: [
    { label: 'LCD 16×2', props: { size: '16x2' } },
    { label: 'LCD 20×4', props: { size: '20x4' } },
  ],
  pins: (props) => {
    const size = lcdSize(props);
    const x0 = lcdHeaderX(size);
    return PIN_NAMES.map((name, i) => ({
      name,
      label: `${i + 1} · ${PIN_LABELS[name] || name}`,
      kind: name === 'VSS' || name === 'K' || name === 'RW' ? 'gnd' : name === 'VDD' || name === 'A' ? 'power' : 'signal',
      x: x0 + i * PITCH,
      y: LCD_PCB_Y + 1.6 + 8.6,
      z: -size.d / 2 + 2.6,
    }));
  },
  footprint: (props) => {
    const s = lcdSize(props);
    return { w: s.w, d: s.d, h: 12 };
  },
  help: 'Conexión típica: VSS y RW a GND, VDD a 5 V, V0 a GND (o a un potenciómetro), A a 5 V y K a GND para la luz de fondo.',
});

definePart({
  type: 'lcd-i2c',
  name: 'LCD de caracteres I²C',
  category: 'luces',
  description: 'Pantalla HD44780 con adaptador PCF8574: solo 4 cables (librería LiquidCrystal_I2C). SDA a A4 y SCL a A5.',
  keywords: 'pantalla display 1602 2004 liquidcrystal_i2c i2c pcf8574',
  props: [
    sizeProp,
    colorProp,
    { key: 'address', label: 'Dirección I²C', type: 'select', default: '0x27', options: [{ value: '0x27', label: '0x27 (PCF8574T)' }, { value: '0x3F', label: '0x3F (PCF8574AT)' }] },
  ],
  variants: [
    { label: 'LCD 16×2 I²C', props: { size: '16x2' } },
    { label: 'LCD 20×4 I²C', props: { size: '20x4' } },
  ],
  pins: (props) => {
    const size = lcdSize(props);
    // Conector macho en ángulo recto en el borde izquierdo del adaptador (debajo de la pantalla).
    const zc = -size.d / 2 + 9.5;
    return ['GND', 'VCC', 'SDA', 'SCL'].map((name, i) => ({
      name,
      label: { GND: 'GND', VCC: 'VCC (5 V)', SDA: 'SDA (datos I²C)', SCL: 'SCL (reloj I²C)' }[name],
      kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
      x: lcdHeaderX(size) - 8.5,
      y: 4.3,
      z: zc + (i - 1.5) * PITCH,
      dir: [-1, 0, 0],
    }));
  },
  footprint: (props) => {
    const s = lcdSize(props);
    return { w: s.w + 1, d: s.d + 1, h: 22 };
  },
});
